/**
 * PersonaContext — exposé global du manifest Cockpit Adaptatif V23.13.
 *
 * Le manifest provient de GET /api/workspace/persona/me et combine :
 *   - le plan de l'org (source de vérité absolue des modules autorisés)
 *   - le sector_package (filtre de présentation)
 *   - le ui_level utilisateur (debutant / intermediaire / expert)
 *
 * Hook : `usePersona()` → { manifest, labelFor, isActionHidden, refresh, loading }.
 * Wrapper composant : `<PersonaAction action="export_pdf">…</PersonaAction>`
 *   masque ses enfants si l'action figure dans `manifest.hidden_actions`.
 *
 * Le provider doit englober TOUTES les routes /app/* afin que toute page puisse
 * consommer le manifest sans refetch. Refresh est appelé après changement de
 * persona via PersonaSwitcher.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const PersonaContext = createContext(null);

const DEFAULT_MANIFEST = {
  sector_package: "default",
  sector_package_label: "Standard",
  ui_level: "intermediaire",
  ui_level_label: "Intermédiaire",
  visible_modules: [],
  visible_modules_count: 0,
  plan_modules_count: 0,
  package_modules_count: 0,
  label_overrides: {},
  hidden_actions: [],
  show_help_tooltips: true,
  show_inline_guides: false,
  show_advanced_params: false,
  show_raw_data: false,
};

export function PersonaProvider({ children }) {
  const [manifest, setManifest] = useState(DEFAULT_MANIFEST);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/workspace/persona/me");
      setManifest({ ...DEFAULT_MANIFEST, ...(r.data || {}) });
      setError(null);
    } catch (e) {
      // En cas d'erreur (404, no org), on garde le manifest par défaut —
      // l'UI reste fonctionnelle, juste sans adaptations.
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Recharge auto sur changement de persona déclenché ailleurs (PersonaSwitcher).
  useEffect(() => {
    const handler = () => refresh();
    window.addEventListener("persona:changed", handler);
    return () => window.removeEventListener("persona:changed", handler);
  }, [refresh]);

  const labelOverrides = manifest.label_overrides || {};
  const hiddenSet = useMemo(
    () => new Set(manifest.hidden_actions || []),
    [manifest.hidden_actions],
  );

  const labelFor = useCallback(
    (moduleKey, fallback) => {
      if (!moduleKey) return fallback;
      const o = labelOverrides[moduleKey];
      return o || fallback;
    },
    [labelOverrides],
  );

  const isActionHidden = useCallback(
    (actionKey) => !!actionKey && hiddenSet.has(actionKey),
    [hiddenSet],
  );

  const value = useMemo(
    () => ({ manifest, labelFor, isActionHidden, refresh, loading, error }),
    [manifest, labelFor, isActionHidden, refresh, loading, error],
  );

  return <PersonaContext.Provider value={value}>{children}</PersonaContext.Provider>;
}

export function usePersona() {
  const ctx = useContext(PersonaContext);
  if (!ctx) {
    // En dehors d'un provider (ex: page publique) — fallback no-op.
    return {
      manifest: DEFAULT_MANIFEST,
      labelFor: (_k, f) => f,
      isActionHidden: () => false,
      refresh: async () => {},
      loading: false,
      error: null,
    };
  }
  return ctx;
}

/**
 * <PersonaAction action="export_pdf"> … </PersonaAction>
 *
 * Wrapper déclaratif : si l'action est dans `hidden_actions` du manifest,
 * les enfants ne sont pas rendus. Aucun fallback affiché par défaut.
 */
export function PersonaAction({ action, children, fallback = null }) {
  const { isActionHidden } = usePersona();
  if (isActionHidden(action)) return fallback;
  return <>{children}</>;
}

/**
 * Notifie tous les consommateurs du context que la persona a changé.
 * À appeler depuis PersonaSwitcher après un PUT /persona/org ou /persona/user.
 */
export function broadcastPersonaChange() {
  window.dispatchEvent(new Event("persona:changed"));
}
